<template>
  <div id="app">
    <DataGetter
      :url="`${apiRoute}/countries`"
      child-element-type="span"
      @fetched="setCountries"
    />
    <ControlGroup v-if="!indicatorName || !countryName">
      <Dropdown
        v-if="!countryName && countries"
        key="countriesdd"
        label="Country"
        v-model="selectedCountry"
        :options="
          countries.map((country) => ({
            label: country.name,
            value: country.stats,
          }))
        "
      />
      <Dropdown
        v-if="!indicatorName"
        key="indicatorsdd"
        label="Indicator"
        v-model="indicator"
        :options="indicators"
      />
    </ControlGroup>
    <DataGetter
      v-if="country"
      :url="`${apiRoute}${indicator.route}`"
      :params="params"
      v-slot="{ data }"
      class="container"
      @fetched="setChartData"
    >
      <DataSlicer
        v-if="indicator.title === 'Oil stocks'"
        :data="data"
        :custom-slicer="oilDataSlicer"
        v-slot="{ slicedData }"
      >
        <ChartLayout
          :title="`Oil stocks of ${country.name}, ${years.first}-${years.last}`"
          :units="indicator.units"
          v-if="slicedData"
        >
          <Chart
            :key="`${country}chart`"
            :options="{
              ...stocksChartOptions,
              tooltip: {
                valueDecimals: 0,
                valueSuffix: ` ${units}`,
                xDateFormat: '%B %Y',
                zIndex: 1000,
              },
              series: slicedData,
            }"
            class="highcharts-outline-black"
          />
          <template #notes v-if="notes">
            <span v-html="notes" />
          </template>
        </ChartLayout>
      </DataSlicer>
      <DataSlicer
        v-else
        :data="data"
        x-axis-dimension="year"
        y-axis-dimension="flowLabel"
        value-dimension="value"
        v-slot="{ slicedData }"
      >
        <ChartLayout
          :title="`${indicator.title}, ${country.name}, ${years.first}-${years.last}`"
          :units="indicator.units"
          :palette="indicator.palette"
          v-if="slicedData"
        >
          <Chart
            :key="`${country.stats}${indicator.title}chart`"
            :options="{
              ...chartOptions,
              tooltip: {
                valueDecimals: 0,
                valueSuffix: ` ${indicator.units}`,
              },
              series: merge(slicedData),
            }"
            class="highcharts-outline-black"
          />
          <template #notes v-if="notes">
            <span v-html="notes" />
          </template>
        </ChartLayout>
      </DataSlicer>
    </DataGetter>
  </div>
</template>

<script>
/* global Highcharts */
import { Chart } from "highcharts-vue";
import {
  DataGetter,
  DataSlicer,
  ControlGroup,
  Dropdown,
} from "@international-energy-agency/vue-ui";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
Highcharts.setOptions({
  lang: {
    numericSymbols: null,
  },
});
export default {
  name: "App",
  components: {
    DataGetter,
    DataSlicer,
    Chart,
    ChartLayout,
    ControlGroup,
    Dropdown,
  },
  props: {
    // Name of indicator (label)
    indicatorName: {
      type: String,
      required: false,
    },
    // STATS name of country except for oil stocks
    countryName: {
      type: String,
      required: false,
    },
  },
  data() {
    const indicators = [
      {
        label: "Oil stocks",
        value: {
          title: "Oil stocks",
          route: "/netimports/",
          countryParam: "country",
          countryProp: "name",
          units: "Days of net imports",
        },
      },
      {
        label: "Natural gas consumption",
        value: {
          title: "Natural gas consumption by sector",
          route: "/stats/",
          countryParam: "countries",
          countryProp: "stats",
          series: "BALANCES",
          flows: [
            "TOTIND",
            "TOTTRANS",
            "RESIDENT",
            "COMMPUB",
            "AGRICULT",
            "FISHING",
            "ONONSPEC",
            "NONENUSE",
            "MAINELEC",
            "MAINCHP",
            "MAINHEAT",
          ],
          products: ["NATGAS"],
          merge: {
            "Electricity and heat generation": [
              "Electricity plants",
              "CHP plants",
              "Heat plants",
            ],
          },
          units: "TJ",
          hideEmpty: true,
        },
      },
      {
        label: "Oil consumption",
        value: {
          title: "Oil products consumption by sector",
          route: "/stats/",
          countryParam: "countries",
          countryProp: "stats",
          series: "BALANCES",
          flows: [
            "TOTIND",
            "TOTTRANS",
            "RESIDENT",
            "COMMPUB",
            "AGRICULT",
            "FISHING",
            "ONONSPEC",
            "NONENUSE",
            "MAINELEC",
            "MAINCHP",
            "MAINHEAT",
          ],
          products: ["TOTPRODS"],
          merge: {
            "Electricity and heat generation": [
              "Electricity plants",
              "CHP plants",
              "Heat plants",
            ],
          },
          units: "TJ",
          hideEmpty: true,
        },
      },
      {
        label: "Electricity production",
        value: {
          title: "Overall electricity production rate",
          route: "/stats/",
          countryParam: "countries",
          countryProp: "stats",
          series: "ELECTRICITYANDHEAT",
          flows: ["IMPORTS", "EHINDPROD", "DISTLOSS", "EXPORTS", "FINCONS"],
          products: ["ELECTR"],
          invert: ["Losses"],
          seperate: {
            series: ["Final consumption"],
            type: "line",
            colorIndex: 5,
            secondAxis: false,
          },
          units: "GWh",
          hideEmpty: true,
        },
      },
      {
        label: "Electricity sources",
        value: {
          title: "Electricity generation by source",
          route: "/stats/",
          countryParam: "countries",
          countryProp: "stats",
          series: "ELECTRICITYANDHEAT",
          flows: [
            "EHCOAL",
            "EHOIL",
            "EHNATGAS",
            "EHNUCLEAR",
            "EHYDRO",
            "EHBIOMASS",
            "EHWASTE",
            "EHSOLARTH",
            "ESOLARPV",
            "EWIND",
            "EHGEOTHERM",
            "ETIDE",
            "EHOTHER",
          ],
          palette: "tints",
          seriesOrder: [
            "Coal",
            "Natural gas",
            "Oil",
            "Biofuels",
            "Waste",
            "Nuclear",
            "Hydro",
            "Wind",
            "Solar PV",
            "Solar thermal",
            "Geothermal",
            "Tide",
            "Other sources",
          ],
          products: ["ELECTR"],
          hideEmpty: true,
          units: "GWh",
        },
      },
    ];
    const oilStocksStart = 1546297200000;
    return {
      apiRoute: "https://api.iea.org",
      selectedCountry: this.countryName,
      countries: [],
      indicator: this.indicatorName
        ? indicators.find((indicator) => indicator.label === this.indicatorName)
            .value
        : indicators[0].value,
      indicators,
      units: "",
      chartData: null,
      chartOptions: ChartOptions.buildOptions({}, "column"),
      startYear: 2000,
      oilStocksStart,
      stocksChartOptions: ChartOptions.buildOptions(
        {
          plotOptions: {
            stacking: "normal",
          },
          xAxis: {
            type: "datetime",
            min: oilStocksStart,
          },
          yAxis: {
            plotLines: [
              {
                value: 90,
                label: {
                  text: "90 days of net imports",
                },
                zIndex: 7,
              },
            ],
          },
        },
        "area"
      ),
    };
  },
  computed: {
    years() {
      const { indicator, chartData, oilStocksStart } = this;
      if (!chartData) return { first: 1990, last: 2020 };
      if (indicator.title === "Oil stocks") {
        const endYearMonth =
          chartData[chartData.length - 1].yearMonth.toString();
        return {
          first: new Date(oilStocksStart).toLocaleString("en-UK", {
            month: "long",
            year: "numeric",
          }),
          last: new Date(
            `${endYearMonth.slice(0, 4)}-${endYearMonth.slice(4)}`
          ).toLocaleString("en-UK", {
            month: "long",
            year: "numeric",
          }),
        };
      }
      let lastYearIndex = chartData.length - 1;
      while (lastYearIndex > 0 && !chartData[lastYearIndex].value) {
        lastYearIndex -= 1;
      }
      return {
        first: chartData[0].year,
        last: chartData[lastYearIndex].year,
      };
    },
    country() {
      const { countries, selectedCountry } = this;
      return countries.find(
        (country) =>
          country.name === selectedCountry || country.stats === selectedCountry
      );
    },
    params() {
      const { indicator, country, startYear } = this;
      if (!country || !indicator) return {};
      return {
        [indicator.countryParam]: country[indicator.countryProp],
        series: indicator.series,
        flows: indicator.flows,
        products: indicator.products,
        startYear,
      };
    },
    notes() {
      const country = this.countries.find(
        (country) => country.name === this.country
      );
      return country ? country.explanation : null;
    },
  },
  methods: {
    setChartData(data) {
      this.chartData = data;
    },
    setUnits(units) {
      this.units = units;
    },
    parseData(point) {
      if (point === "-") return null;
      return parseInt(point);
    },
    setCountries(countries) {
      this.countries = countries;
    },
    merge(slicedData) {
      const { merge, seperate, invert, hideEmpty, seriesOrder } =
        this.indicator;
      let exclude = [];
      const returnData = [...slicedData];
      if (merge) {
        Object.keys(merge).forEach((mergedSeries) => {
          const toCombine = merge[mergedSeries];
          exclude = [...exclude, ...toCombine];
          returnData.unshift({
            name: mergedSeries,
            data: returnData
              .filter((series) => toCombine.includes(series.name))
              .reduce((cdata, series) => {
                series.data.forEach((point, i) => {
                  if (point[1] !== null) {
                    if (cdata[i]) {
                      cdata[i][1] += -point[1];
                    } else {
                      cdata[i] = [point[0], -point[1]];
                    }
                  }
                });
                return cdata;
              }, []),
          });
        });
      }
      return (
        returnData
          // Handle exluded and empty
          .filter(
            (series) =>
              !exclude.includes(series.name) &&
              (!hideEmpty || series.data.some((point) => point[1] !== null))
          )
          .map((series, i) => {
            const seriesIndex =
              seriesOrder && seriesOrder.includes(series.name)
                ? seriesOrder.indexOf(series.name)
                : i;
            return {
              ...series,
              data:
                invert && invert.includes(series.name)
                  ? series.data.map((point) => [
                      point[0],
                      point[1] !== null ? -point[1] : null,
                    ])
                  : series.data,
              type:
                seperate && seperate.series.includes(series.name)
                  ? seperate.type
                  : undefined,
              colorIndex:
                seperate && seperate.series.includes(series.name)
                  ? seperate.colorIndex || series.colorIndex
                  : seriesIndex,
              index: seriesIndex,
            };
          })
          // handle inverted series
          .map((series) =>
            invert && invert.includes(series.name)
              ? {
                  ...series,
                  data: series.data.map((point) => [
                    point[0],
                    point[1] !== null ? -point[1] : null,
                  ]),
                }
              : series
          )
          // handle seperate series
          .map((series) =>
            seperate && seperate.series.includes(series.name)
              ? {
                  ...series,
                  type: seperate.type,
                  colorIndex: seperate.colorIndex || series.colorIndex,
                }
              : series
          )
      );
    },
    oilDataSlicer(data) {
      const series = [
        {
          name: "Public",
          data: [],
        },
        {
          name: "Public, held abroad",
          data: [],
        },
        {
          name: "Industry",
          data: [],
        },
        {
          name: "Industry, held abroad",
          data: [],
        },
      ];
      data.forEach((row) => {
        const year = row.yearMonth.toString().slice(0, 4);
        const month = row.yearMonth.toString().slice(4);
        const date = new Date(`${month}-02-${year}`).getTime();
        series[0].data.push([date, this.parseData(row.publicData)]);
        series[1].data.push([date, this.parseData(row.abroadPublic)]);
        series[2].data.push([date, this.parseData(row.industry)]);
        series[3].data.push([date, this.parseData(row.abroadIndustry)]);
      });
      return {
        slicedData: series,
      };
    },
  },
};
</script>
